/**
 * permissionStore.ts
 *
 * Zustand store for runtime feature-level permissions.
 *
 * Data model: per-company → per-plugin → { view, manage, delete } boolean flags.
 * Populated by decoding the JWT ID token (adminLevel/roleScopes) + fetching
 * from the backend API (per-company plugin features).
 */

import { create } from 'zustand';
import { SYSTEM_ADMIN_TENANT_UUID } from '../auth/AuthProvider';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** High-level claims decoded from the JWT ID token. */
export interface JwtClaims {
  adminLevel: number;
  roleScopes: string[];
}

/** A single feature permission entry (one plugin, one company). */
export interface FeaturePermissionEntry {
  /** e.g. "cms", "website-builder", "crm" — maps to the plugin/module name */
  module: string;
  /** Human-readable label for display in UI tables */
  moduleName?: string;
  permissions: {
    view: boolean;
    manage: boolean;
    delete: boolean;
  };
}

/**
 * Permission state shape.
 * - jwtClaims: decoded from ID token on init/refresh
 * - currentCompanyId: the active company context (from AuthProvider)
 * - featuresByCompany: per-company map of enabled feature codes
 */
export interface PermissionState {
  // ── State ────────────────────────────────────────────────────────────────

  jwtClaims: JwtClaims | null;

  /** Currently selected company / tenant ID. Null for system-level context. */
  currentCompanyId: string | null;

  /** companyId → Set of enabled feature codes (e.g., "cms:view", "website:manage") */
  featuresByCompany: Map<string, Set<string>>;

  /** Whether permission data has ever been loaded (distinguishes "loading" from "empty"). */
  permissionsLoaded: boolean;

  /** ISO timestamp of the last successful load — used by HasPermission for loading-state gating. */
  permissionsLoadedAt: number | null;

  /** The shellRoute of the currently loaded plugin (for auto-prefixing feature codes). Set by ApplicationContainer when a plugin mounts. */
  currentShellRoute: string | null;

  // ── Actions (imperative — called directly from AuthProvider / lifecycle) ──

  setJwtClaims: (claims: JwtClaims) => void;
  setCurrentCompanyId: (companyId: string | null) => void;
  setFeaturesForCompany: (companyId: string, features: Set<string>) => void;
  clearFeaturesForCompany: (companyId: string) => void;
  markPermissionsLoaded: () => void;
  /** Reset permissionsLoaded to false — call before starting a fresh permission fetch (e.g. company switch) so guards wait for updated data. */
  resetPermissionsLoaded: () => void;
  setCurrentShellRoute: (shellRoute: string | null) => void;
  resetAll: () => void;

  // ── Derived selectors (computed inline — no memo needed for small sets) ──

  /** Check if a global feature code is enabled across all companies. */
  hasFeature: (featureCode: string) => boolean;

  /** Check if the user can perform an action on a specific company's plugin. Defaults to currentCompanyId if not provided. */
  canManage: ({ companyId, featureCode }: { companyId?: string; featureCode: string }) => boolean;

  /** Check if admin level meets or exceeds a threshold. */
  isAdminAtLeast: (level: number) => boolean;

  /** Get all feature codes enabled for the current company's active plugin, filtered by shellRoute prefix. Returns empty array if no shell route is set. */
  listPermissions: () => string[];

  /** Check if the user has ANY permission assigned. Returns true if user has at least one permission, false if none. */
  hasPermissionAssign: () => boolean;

  /** Get all feature codes for a specific module name (by shellRoute), regardless of currentShellRoute. */
  listPermissionsForModule: (moduleName: string) => string[];
}

// ---------------------------------------------------------------------------
// Store definition
// ---------------------------------------------------------------------------

export const usePermissionStore = create<PermissionState>((set, get) => ({
  jwtClaims: null,
  currentCompanyId: null,
  featuresByCompany: new Map(),
  permissionsLoaded: false,
  permissionsLoadedAt: null,
  currentShellRoute: null,

  // ── Mutations ────────────────────────────────────────────────────────────

  setJwtClaims: (claims: JwtClaims) => {
    set({ jwtClaims: claims });
  },

  setCurrentCompanyId: (companyId: string | null) => {
    set({ currentCompanyId: companyId || SYSTEM_ADMIN_TENANT_UUID });
  },

  setFeaturesForCompany: (companyId: string, features: Set<string>) => {
    const next = new Map(get().featuresByCompany);
    next.set(companyId, features);
    set({ featuresByCompany: next });
  },

  clearFeaturesForCompany: (companyId: string) => {
    const next = new Map(get().featuresByCompany);
    next.delete(companyId);
    set({ featuresByCompany: next });
  },

  markPermissionsLoaded: () => {
    set({ permissionsLoaded: true, permissionsLoadedAt: Date.now() });
  },

  resetPermissionsLoaded: () => {
    set({ permissionsLoaded: false, permissionsLoadedAt: null });
  },

  setCurrentShellRoute: (shellRoute: string | null) => {
    set({ currentShellRoute: shellRoute });
  },

  resetAll: () => {
    set({
      jwtClaims: null,
      currentCompanyId: null,
      featuresByCompany: new Map(),
      permissionsLoaded: false,
      permissionsLoadedAt: null,
      currentShellRoute: null,
    });
  },

  // ── Derived selectors (computed inline — no memo needed for small sets) ──

  /**
   * Check if a global feature code is enabled across all companies.
   *
   * Feature codes MUST have exactly 4 segments: blx:{route}:{feature}:{action}
   * (e.g., "blx:cms-local:Posts:view", "blx:report-viewer:Leads:manage").
   *
   * Auto-prefixing: if the featureCode doesn't start with "blx:", and there's an active
   * currentShellRoute, it will be prefixed as "blx:{shellRoute}:{featureName}".
   * The caller MUST include the action suffix — e.g., hasFeature("Posts:view") becomes
   * "blx:cms-local:Posts:view". Passing a bare name like "Posts" is rejected (3 segments
   * instead of 4) with a console.warn.
   *
   * If the code starts with "blx:" but doesn't match currentShellRoute, it's used as-is —
   * this lets callers check permissions for other plugins' features by passing the full
   * 4-segment code (e.g., hasFeature("blx:catalog:CatalogManager:view")).
   *
   * Resolution order:
   *   1. Validate 4-segment format → reject with warn if wrong.
   *   2. adminLevel >= 3 → grant everything (super-admin shortcut).
   *   3. Current company context → check its feature set first (most common case).
   *   4. Fallback scan across ALL companies' feature sets.
   */
  hasFeature: (featureCode: string): boolean => {
    const state = get();
    // Auto-prefix if code doesn't start with "blx:" and we have an active shell route
    let resolvedCode = featureCode;
    if (!featureCode.startsWith('blx:') && state.currentShellRoute) {
      resolvedCode = `blx:${state.currentShellRoute}:${featureCode}`;
    }

    // Enforce 4-segment format: blx:{route}:{feature}:{action}
    const segments = resolvedCode.split(':');
    if (segments.length !== 4) {
      console.warn(
        `[PermissionStore] hasFeature("${featureCode}") → rejected: expected 4 segments (blx:route:feature:action), got ${segments.length} ("${resolvedCode}"). Pass an action suffix, e.g. "Posts:view" not "Posts".`,
      );
      return false;
    }

    // Check the active company context first.
    if (state.currentCompanyId && state.featuresByCompany.has(state.currentCompanyId)) {
      const features = state.featuresByCompany.get(state.currentCompanyId)!;
      // console.log(
      //   `[PermissionStore] hasFeature("${featureCode}") → resolved to "${resolvedCode}", currentShellRoute="${state.currentShellRoute}", currentCompanyId="${state.currentCompanyId}", features.size=${features.size}, found=${features.has(resolvedCode)}`,
      // );

      // Direct match (exact code including action suffix like "blx:cms-local:Posts:view").
      if (features.has(resolvedCode)) return true;
    }

    // Fallback: check all companies for this feature code.
    for (const features of state.featuresByCompany.values()) {
      if (features.has(resolvedCode)) return true;
    }

    console.warn(
      `[PermissionStore] hasFeature("${featureCode}") → false (resolved to "${resolvedCode}", shellRoute="${state.currentShellRoute}", companies=${state.featuresByCompany.size})`,
    );
    return false;
  },

  /**
   * Check if the user can manage a feature within a specific company.
   * Accepts flexible input:
   * - Bare name: `'Posts'` → checks for ANY action variant (`view|manage|delete`)
   * - With action: `'Posts:manage'` → checks that exact action
   * - Full code: `'blx:cms-local:Posts:manage'` → uses the full 4-segment code as-is
   *
   * Auto-prefixing applies when no `blx:` prefix is given and currentShellRoute is set.
   * Defaults to currentCompanyId if not explicitly provided.
   */
  canManage: ({ companyId, featureCode }: { companyId?: string; featureCode: string }): boolean => {
    const state = get();

    // Resolve target company — explicit arg wins, otherwise fall back to current context.
    const targetCompany = companyId ?? state.currentCompanyId;
    if (!targetCompany) return false;

    // Global super-admin has everything.
    const adminLevel2 = state.jwtClaims?.adminLevel;
    if (typeof adminLevel2 === 'number' && adminLevel2 >= 3) return true;

    // Auto-prefix if code doesn't start with "blx:" and we have an active shell route
    let resolvedCode = featureCode;
    if (!featureCode.startsWith('blx:') && state.currentShellRoute) {
      resolvedCode = `blx:${state.currentShellRoute}:${featureCode}`;
    }

    const segments2 = resolvedCode.split(':');

    // Check this specific company's features.
    const features = state.featuresByCompany.get(targetCompany);
    if (!features) return false;

    // 4-segment exact match (e.g., "blx:cms-local:Posts:manage") — direct check.
    if (segments2.length === 4 && features.has(resolvedCode)) return true;

    // 3-segment code with action suffix (e.g., "blx:cms-local:Posts:manage" after prefixing)
    // — already handled above by the 4-segment check.

    // 3-segment bare feature name (e.g., "blx:cms-local:Posts") → check all action variants.
    if (segments2.length === 3) {
      for (const action of ['view', 'manage', 'delete'] as const) {
        if (features.has(`${resolvedCode}:${action}`)) return true;
      }
      return false;
    }

    // Anything else (1-segment, 2-segment without prefixing, etc.) — not actionable.
    console.warn(
      `[PermissionStore] canManage("${featureCode}") → rejected: expected "Posts" or "Posts:manage" or "blx:cms-local:Posts:manage", got "${resolvedCode}" (${segments2.length} segments).`,
    );
    return false;
  },

  isAdminAtLeast: (level: number): boolean => {
    const claims = get().jwtClaims;
    if (!claims) return false;
    return claims.adminLevel >= level;
  },

  listPermissions: (): string[] => {
    const state = get();
    // No shell company set → nothing to filter by
    if (!state.currentCompanyId) return [];

    // Get features for the current company and filter by shellRoute prefix
    const features = state.featuresByCompany.get(state.currentCompanyId);
    if (!features) return [];

    const prefix = !state.currentShellRoute ? 'blx:' : `blx:${state.currentShellRoute}:`;
    // Only return 4-segment codes (blx:route:feature:action) — bare feature names are not valid.
    const result = [...features].filter((code) => {
      if (!code.startsWith(prefix)) return false;
      return code.split(':').length === 4;
    });
    return result;
  },

  /**
   * Check if the user has ANY permission assigned.
   * Returns true if user has at least one permission, false if none.
   *
   * Usage: `hasPermissionAssign()` → true/false
   *
   * This is a simple check for whether the user has any permissions at all,
   * useful for showing/hiding permission-dependent UI.
   */
  hasPermissionAssign: (): boolean => {
    const state = get();
    // Require company context
    if (!state.currentCompanyId) {
      return false;
    }

    const features = state.featuresByCompany.get(state.currentCompanyId);
    if (!features || features.size === 0) {
      return false;
    }

    // Check across ALL plugins/shellRoutes — not just currentShellRoute, which is stale
    // outside of an active plugin (e.g. on Home/Dashboard) since it's only set on plugin mount.
    const hasAnyPermission = [...features].some((code) => {
      if (!code.startsWith('blx:')) return false;
      return code.split(':').length === 4;
    });
    return hasAnyPermission;
  },

  /** Get all feature codes for a specific module name (by shellRoute), regardless of currentShellRoute. */
  listPermissionsForModule: (moduleName: string): string[] => {
    const state = get();
    if (!state.currentCompanyId) return [];

    // Try to resolve the shellRoute from the module name — use it as-is (it should be a shellRoute like "cms-local")
    const features = state.featuresByCompany.get(state.currentCompanyId);
    if (!features) return [];

    const prefix = `blx:${moduleName}:`;
    // Only return 4-segment codes (blx:route:feature:action).
    const result = [...features].filter((code) => {
      if (!code.startsWith(prefix)) return false;
      return code.split(':').length === 4;
    });
    // console.log(
    //   '[PermissionStore] listPermissionsForModule(): prefix=',
    //   prefix,
    //   'result.length=',
    //   result.length,
    // );
    return result;
  },
}));
